import { ButtonTypes } from "devextreme-react/button";
import { DropDownButtonRef } from "devextreme-react/drop-down-button";
import { ListRef } from "devextreme-react/list";
import { TreeViewTypes } from "devextreme-react/tree-view";
import React from "react";

export interface AppHeaderProps {
  menuToggleEnabled: boolean;
  title?: string;
  toggleMenu: (e: ButtonTypes.ClickEvent) => void;
  className?: string;
}

export interface SideNavigationMenuProps {
  selectedItemChanged: (e: TreeViewTypes.ItemClickEvent) => void;
  openMenu: (e: React.PointerEvent) => void;
  compactMode: boolean;
  onMenuReady: (e: TreeViewTypes.ContentReadyEvent) => void;
}

export interface UserPanelProps {
  menuMode: "context" | "list";
}

export interface UserMenuSectionProps {
  dropDownButtonRef?: React.RefObject<DropDownButtonRef>;
  listRef?: React.RefObject<ListRef>;
}

export interface User {
  email: string;
  id: number;
  companyId?: number;
  roles?: string[];
  requiresTwoFactorAuthentication?: boolean;
  isLockedOut?: boolean;
  confirmationRequired?: boolean;
  peselLoginBlock?: boolean;
  firstChangePassword?: boolean;
  enable2Fa?: boolean;
  employeeId?: number;
  contractType?: number;
  isTimeManager?: boolean;
  firstName?: string;
  lastName?: string;
  termsOfUseAccepted: boolean;
  generalDataProtectionRegulationAccepted: boolean;
  processingOfEmailAddressAccepted: boolean;
}

export type AuthContextType = {
  user?: User;
  signIn: (
    email: string,
    password: string,
    companyId: number,
    rememberMe: boolean,
    requiresTwoFactorAuthentication?: boolean,
    isLockedOut?: boolean,
    confirmationRequired?: boolean,
    peselLoginBlock?: boolean,
    firstChangePassword?: boolean,
    enable2Fa?: boolean,
  ) => Promise<{
    isOk: boolean;
    data?: User;
    message?: string;
    requiresTwoFactorAuthentication?: boolean;
    isLockedOut?: boolean;
    confirmationRequired?: boolean;
    peselLoginBlock?: boolean;
    firstChangePassword?: boolean;
    enable2Fa?: boolean;
  }>;
  signOut: () => void;
  loading: boolean;
  signInAuth: () => Promise<{
    isOk: boolean;
    data?: User;
    error?: string;
  }>;
  refreshUser: () => Promise<void>;
  setRegulationAccepted: (regulation: Regulation) => void;
};

export interface SingleCardProps {
  title?: string;
  description?: string;
}

export type Handle = () => void;

interface NavigationData {
  currentPath: string;
}

export type NavigationContextType = {
  setNavigationData?: ({ currentPath }: NavigationData) => void;
  navigationData: NavigationData;
};

export type WorkPlan = {
  date: string;
  dayType: 1 | 2 | 3;
  firstLine: string;
  secondLine: string;
  affectedDays: boolean;
  message: string;
};

export type ManagerSubordinate = {
  companyId: number;
  employeeId: number;
  firstName: string;
  lastName: string;
};

export type Department = {
  id: number;
  name: string;
};

export type Project = {
  id: number;
  acronym: string;
  companyName: string;
};

export type Role = {
  id: number;
  name: string;
};
export type WorkPlanSummary = {
  totalHours: string;
  totalDays: number;
};
export type CollectiveWorkPlan = {
  employee: {
    id: number;
    companyId: number;
    contractType: number;
    code: string;
    firstName: string;
    lastName: string;
  };
  days: {
    date: string;
    dayType: 1 | 2 | 3;
    firstLine: string;
    secondLine: string;
    selected: boolean;
  }[];
  violations: {
    affectedDays: string[];
    message: string;
  }[];
  summary: WorkPlanSummary;
};

export interface PagedWorkPlanResultManager {
  totalCount: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
  items: CollectiveWorkPlan[];
}

export type TypeOfWork = {
  id: number;
  name: string;
};

export type TypeOfDayOff = {
  id: number;
  name: string;
};

export type WorkPlanEmployeeWithDates = {
  employee: {
    companyId: number;
    employeeId: number;
    name: string;
  };
  dates: Date[];
};

export type WorkPlanRequestEntry = {
  employee: {
    companyId: number;
    employeeId: number;
  };
  dates: string[];
  skipDaysOffAndHolidays: boolean;
  typeOfDay: number;
  kindOfDayOff: number | null;
  entrances: {
    from: string;
    to: string;
    typeOfWork: number;
    departmentId: number;
    projectId: number;
    note: string;
  }[];
};

export interface Announcement {
  id: number;
  projects: { projectId: number; acronym: string }[];
  roles: { id: number; name: string }[];
  createdBy: string;
  updatedBy: string | null;
  title: string;
  content: string;
  dateCreated: string;
  dateModified: string | null;
  files: { id: number; fileName: string; url: string }[];
  employeeId: number;
}

export interface Paginated<T> {
  items: T[];
  pageNumber: number;
  pageSize: number;
  totalItems: number;
}

export enum RoleEnum {
  Pracownik = "Pracownik",
  Majster = "Majster",
  Manager = "Manager",
  Kierownik = "Kierownik",
  KadrySpolki = "KadrySpolki",
  AdministratorSpolki = "AdministratorSpolki",
  AdministratorGlowny = "AdministratorGlowny",
}

export type SummaryOfPresencesHoursInProjectEntry = {
  companyId: number;
  employeeId: number;
  code: string;
  lastName: string;
  firstName: string;
  hours: number;
};

export enum CompanyEnum {
  "INVENIO" = 1,
  "INVENIO QD" = 2,
  "QUALITY AND HUMAN SERVICES" = 3,
  "IPSUPPORT" = 4,
}

export type UpdatePresencePositionType = {
  id: number;
  date: Date;
  from: string;
  to: string;
  projectId: number;
  project: string;
  note: string;
};

export type PresencePosition = {
  id: number;
  fromHour: Date;
  toHour: Date;
  to: Date;
  projectId: number;
  project: string;
  note: string;
  confirmed: boolean;
};

export type EmployeeToProject = {
  employeeId: number;
  companyId: number;
};

export type DocumentSendRequestWithNames = {
  id: number;
  applicant: string | null;
  applicantEmailAddress: string;
  documentTypeWithDate: string;
  attendant: string | null;
  attendantComment: string | null;
  dateOfCompletion: string | null;
  dateCreated: string;
};

export enum ShortenCompanyNames {
  "INV" = 1,
  "QD" = 2,
  "QHS" = 3,
  "IPS" = 4,
}

export type PerformanceToConfirmEntry = {
  employee: {
    id: string;
    fullName: string;
  };
  presencePositions: {
    id: number;
    from: string;
    to: string;
    project: {
      id: number;
      name: string;
    };
    note: string | null;
  }[];
};

export type PresenceAvailableEmployee = {
  companyId: number;
  employeeId: number;
  firstName: string;
  lastName: string;
};

export type PresenceAvailableProject = {
  projectId: number;
  name: string;
};

export type CalculateWorkingTimeSummaryResult = {
  employeeFullName: string;
  civilContractNumber: string;
  timeInMinutes: number;
};

export type CEID = {
  companyId: number;
  employeeId: number;
};

export type AnnualLeaveLimitEntry = CEID & {
  numberOfDays: number;
  numberOfHours: number;
};

export interface AdministratorPanelUser {
  id: number;
  email: string;
  companyId: number | null;
  employeeId: number | null;
  code: string | null;
  firstName: string | null;
  lastName: string | null;
  isManager: boolean;
  deactivated: boolean;
  role: {
    id: number;
    name: string | null;
  } | null;
  twoFactorEnabled: boolean;
  supervisorFullName: string | null;
}

export type ProjectSupervisor = CEID & {
  fullName: string;
};
export interface PresencePlan {
  employee: {
    id: number;
    code: string;
    firstName: string;
    lastName: string;
  };
  sum: {
    sumaDni: number;
    sumaGodzin: number;
  };
  days: {
    date: string;
    nameType: number | null;
    dayType: number | null;
    hours: {
      fromHour: string;
      toHour: string;
      duration: string;
      note: string;
      project: string;
      confirmed?: number;
      synch?: boolean;
      transferred?: boolean;
    };
  }[];
  violations: {
    affectedDays: string[];
    message: string;
  }[];
}
export interface PagedPresencePlanResult {
  totalCount: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
  items: PresencePlan[];
}

export type Regulation =
  | "TermsOfUse"
  | "GeneralDataProtectionRegulation"
  | "ProcessingOfEmailAddress";
